import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

type DeltaCallback = (delta: number) => void;

export function useAdjustOnScrollAndDrag(
  containerRef: RefObject<HTMLElement | null>,
  onDelta: DeltaCallback,
  step: number = 0.1
) {
  const callbackRef = useRef<DeltaCallback>(onDelta);
  callbackRef.current = onDelta;

  const initialPointerY = useRef<number | null>(null);

  useEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const delta = event.deltaY < 0 ? step : -step;
      callbackRef.current(delta);
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse') {
        return;
      }

      initialPointerY.current = event.clientY;
      element.setPointerCapture(event.pointerId);
      event.preventDefault();
    };

    const handlePointerMove = (event: PointerEvent) => {
      const startY = initialPointerY.current;

      if (startY === null) {
        return;
      }

      const difference = startY - event.clientY;
      const delta = difference > 0 ? step : -step;
      callbackRef.current(delta);
      initialPointerY.current = event.clientY;
      event.preventDefault();
    };

    const handlePointerUp = (event: PointerEvent) => {
      initialPointerY.current = null;
      element.releasePointerCapture(event.pointerId);
    };

    element.addEventListener('wheel', handleWheel, { passive: false });
    element.addEventListener('pointerdown', handlePointerDown, {
      passive: false
    });
    element.addEventListener('pointermove', handlePointerMove, {
      passive: false
    });
    element.addEventListener('pointerup', handlePointerUp);
    element.addEventListener('pointercancel', handlePointerUp);

    return () => {
      element.removeEventListener('wheel', handleWheel);
      element.removeEventListener('pointerdown', handlePointerDown);
      element.removeEventListener('pointermove', handlePointerMove);
      element.removeEventListener('pointerup', handlePointerUp);
      element.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [containerRef, step]);
}
